import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useData } from './DataProvider';
import { loadAcrRules } from '@appesteem/aeshared-js/rules';

const SCENARIO_OPTIONS = [
    'Install', 'Inline offers', 'In-bundle offers', 'Bundler-made offers', 'Internal offers', 'Software', 'Uninstall', 'Ads inside app', 'Injected ads', 'Injected interstitials'
];
const ABOUT_OPTIONS = [
    { key: 'isBundler', label: 'Is app a bundler?' },
    { key: 'injectsOrBlocks', label: 'Does app inject or block in other apps?' },
    { key: 'isSystool', label: 'Is app a systool?' }
];

export default function HintsPanel({ style, inline = false }) {
    const [error, setError] = useState(null);
    const [selectedScenarios, setSelectedScenarios] = useState(SCENARIO_OPTIONS);
    const [about, setAbout] = useState({ isBundler: false, injectsOrBlocks: false, isSystool: false });
    const [open, setOpen] = useState(false);
    const {hints, setHintsData} = useData();

    useEffect(() => {
        const controller = new AbortController();
        loadAcrRules({ signal: controller.signal })
            .then(setHintsData)
            .catch(e => {
                if (e.name === 'AbortError') return;
                setError(e.message);
            });
        return () => controller.abort();
    }, [setHintsData]);

    const handleScenarioChange = (scenario) => {
        setSelectedScenarios(prev =>
            prev.includes(scenario) ? prev.filter(s => s !== scenario) : [...prev, scenario]
        );
    };

    const handleAboutChange = (key) => {
        setAbout(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const ABOUT_KEYWORDS = {
        isBundler: 'is app a bundler?',
        injectsOrBlocks: 'does app inject or block in other apps?',
        isSystool: 'is app a systool?',
    };

    const filteredHints = hints.filter(hint => {
        if (!hint.scenarios || !Array.isArray(hint.scenarios)) return false;
        const scenarioMatch = hint.scenarios.some(s => selectedScenarios.includes(s));
        if (!scenarioMatch) return false;
        const checkedAboutKeys = Object.keys(about).filter(key => about[key]);
        if (checkedAboutKeys.length === 0) return true;
        const condQ = (hint.conditionalQuestion || '').toLowerCase().trim();
        return checkedAboutKeys.every(key => {
            const keyword = ABOUT_KEYWORDS[key].toLowerCase().trim();
            return condQ === keyword;
        });
    });

    const noFiltersSelected = selectedScenarios.length === 0 && !Object.values(about).some(Boolean);

    if (error) return <Typography color="error">{error}</Typography>;
    if (!hints.length) return <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading hints...</Typography>;

    const hintsContent = (
        <>
            <Box mb={1} sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
                <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', fontWeight: 'bold' }}>Scenarios:</Typography>
                {SCENARIO_OPTIONS.map(option => (
                    <FormControlLabel
                        key={option}
                        control={<Checkbox size="small" checked={selectedScenarios.includes(option)} onChange={() => handleScenarioChange(option)} />}
                        label={<Typography variant="body2">{option}</Typography>}
                        sx={{ mr: 1 }}
                    />
                ))}
            </Box>
            <Box mb={1} sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
                <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', fontWeight: 'bold' }}>About the app:</Typography>
                {ABOUT_OPTIONS.map(opt => (
                    <FormControlLabel
                        key={opt.key}
                        control={<Checkbox size="small" checked={about[opt.key]} onChange={() => handleAboutChange(opt.key)} />}
                        label={<Typography variant="body2">{opt.label}</Typography>}
                        sx={{ mr: 1 }}
                    />
                ))}
            </Box>
            {noFiltersSelected ? (
                <Box my={4} textAlign="center">
                    <Typography color="text.secondary" variant="body2">
                        Select at least one scenario or about filter to view hints.
                    </Typography>
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, maxHeight: '100%' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ verticalAlign: 'top' }}>Requirement</TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>Prescription</TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>Deceptor?</TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>Good Example(s)</TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>Bad Example(s)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredHints.map((hint, idx) => (
                                <TableRow key={hint.id || idx}>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        {hint.requirement || ''}
                                        {hint.details && (
                                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 0.5 }}>
                                                {hint.details}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        {hint.prescription || ''}
                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 0.5 }}>
                                            Applies to: {hint.scenarios ? hint.scenarios.join(', ') : ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        {hint.isDeceptor === true ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                <WarningAmberIcon color="warning" fontSize="small" />
                                            </Box>
                                        ) : null}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', minWidth: 120 }}>
                                        {Array.isArray(hint.goodExamples) && hint.goodExamples.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {hint.goodExamples.map((url) => (
                                                    <Box key={url} component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ mb: 0.5 }}>
                                                        <img src={url} alt="Good example" style={{ maxWidth: 100, maxHeight: 60, borderRadius: 4, border: '1px solid #eee' }} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : null}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', minWidth: 120 }}>
                                        {Array.isArray(hint.badExamples) && hint.badExamples.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {hint.badExamples.map((url) => (
                                                    <Box key={url} component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ mb: 0.5 }}>
                                                        <img src={url} alt="Bad example" style={{ maxWidth: 100, maxHeight: 60, borderRadius: 4, border: '1px solid #eee' }} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );

    if (inline) {
        return (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', p: 1.5 }}>
                {hintsContent}
            </Box>
        );
    }

    return (
        <Box style={{ ...style }}>
            <Button size="small" onClick={() => setOpen(true)} sx={{ py: 0.5, minWidth: 0, minHeight: 28 }} startIcon={<RuleOutlinedIcon sx={{ fontSize: 18 }} />}>
                ACRs
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth
                PaperProps={{ sx: { width: '90vw', height: '90vh', maxWidth: 'none', maxHeight: 'none' } }}
            >
                <DialogTitle>All Hints ({filteredHints.length})</DialogTitle>
                <DialogContent sx={{ height: 'calc(90vh - 70px)', display: 'flex', flexDirection: 'column', p: 3 }}>
                    {hintsContent}
                    <Box mt={2} textAlign="right">
                        <Button onClick={() => setOpen(false)} variant="contained">Close</Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
